import type { IItemView, IPersonView } from './model';

/** IRCC's upload limit per file for most portals (the newer IRCC portal allows 5 MB). */
export const IRCC_MAX_BYTES: number = 4 * 1024 * 1024;

export interface IPackageFile {
  /** The attached document in the library. */
  sourceId: string;
  sourceName: string;
  /** Name for the copy in the submission package. */
  name: string;
  itemLabel: string;
  person: string;
  size?: number;
  warnings: string[];
}

/** Plain letters, digits, spaces, dots, dashes, underscores and commas: what IRCC's portals take (no brackets, # or accents). */
export function irccSafeName(s: string): string {
  return s
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9 ._\-,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extensionOf(name: string): string {
  const m: RegExpMatchArray | null = name.match(/\.([A-Za-z0-9]{1,5})$/);
  return m ? `.${m[1].toLowerCase()}` : '';
}

/**
 * The submission package: every document attached to a received item, named
 * "01 {Person} - {Item}.pdf" (or "01 {Item}.pdf" for the whole file), numbered in checklist order so the
 * folder lists them that way, with
 * warnings for files too big for IRCC's portals. Copies are made; the originals stay as they are.
 */
export function buildPackage(items: IItemView[], people: IPersonView[], sizes: { [id: string]: number | undefined }, sections: string[]): IPackageFile[] {
  const personName = (key: string): string => people.filter(p => p.key === key)[0]?.name ?? '';
  const order = (s: string): number => (sections.indexOf(s) === -1 ? sections.length : sections.indexOf(s));
  const used: { [lower: string]: number } = {};
  const out: IPackageFile[] = [];
  const received: IItemView[] = items
    .filter(i => i.status === 'Received' && i.documents.length)
    .map((i, n) => ({ i, n }))
    .sort((a, b) => order(a.i.section) - order(b.i.section) || a.n - b.n)
    .map(x => x.i);
  for (const i of received) {
    const person: string = personName(i.personKey);
    const label: string = person ? i.label.replace(` (${person})`, '') : i.label;
    i.documents.forEach(d => {
      const base: string = irccSafeName(person ? `${person} - ${label}` : label).slice(0, 100).trim();
      const ext: string = extensionOf(d.name);
      const n: number = (used[`${base}${ext}`.toLowerCase()] ?? 0) + 1;
      used[`${base}${ext}`.toLowerCase()] = n;
      const place: string = String(out.length + 1).padStart(2, '0');
      const name: string = `${place} ${base}${n > 1 ? ` - ${n}` : ''}${ext}`;
      const size: number | undefined = sizes[d.id];
      const warnings: string[] = [];
      if (size !== undefined && size > IRCC_MAX_BYTES) {
        warnings.push(`${(size / 1024 / 1024).toFixed(1)} MB: over IRCC's 4 MB limit per file. Compress or split it before uploading.`);
      }
      if (!ext || ['.pdf', '.jpg', '.jpeg', '.png', '.tif', '.tiff', '.doc', '.docx'].indexOf(ext) === -1) {
        warnings.push('IRCC accepts PDF, JPG, PNG, TIFF, DOC and DOCX files.');
      }
      out.push({ sourceId: d.id, sourceName: d.name, name, itemLabel: label, person, size, warnings });
    });
  }
  return out;
}
